import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { useTranslation } from '../../i18n/useTranslation';
import type { ResolvedParameterTarget } from '../../services/parameterTargetService';
import { formatTargetNumber } from './formatTarget';
import './ParameterTargetCard.css';

export interface ParameterTargetCardProps {
  target: ResolvedParameterTarget;
  onEdit: (target: ResolvedParameterTarget) => void;
}

/** One parameter: the target range in force and its source as text (never colour only). */
export function ParameterTargetCard({ target, onEdit }: ParameterTargetCardProps) {
  const { t, i18n } = useTranslation();
  const fmt = (n: number) => formatTargetNumber(n, i18n.language);
  const name = String(t(`measurement.history.parameterNames.${target.parameterId}`));
  const isSetPoint = target.source === 'set_point';

  return (
    <li className="parameter-target-card" data-testid={`parameter-target-${target.parameterId}`}>
      <Card>
        <Stack gap="2">
          <Heading level={3}>{name}</Heading>
          <Text data-testid="parameter-target-range">
            {t('paramTargets.range.display', { min: fmt(target.min), max: fmt(target.max), unit: target.unit })}
          </Text>
          {isSetPoint && target.setPoint !== null && target.tolerance !== null && (
            <Text color="muted">
              {t('paramTargets.range.setPoint', { setPoint: fmt(target.setPoint), tolerance: fmt(target.tolerance), unit: target.unit })}
            </Text>
          )}
          <Inline gap="2" align="center" wrap>
            <Badge tone={isSetPoint ? 'success' : 'info'} data-testid="parameter-target-source">
              {isSetPoint ? t('paramTargets.source.setPoint') : t('paramTargets.source.default')}
            </Badge>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onEdit(target)}
              aria-label={t('paramTargets.action.editSetPointFor', { parameter: name })}
              data-testid="parameter-target-edit"
            >
              {isSetPoint ? t('paramTargets.action.editSetPoint') : t('paramTargets.action.addSetPoint')}
            </Button>
          </Inline>
        </Stack>
      </Card>
    </li>
  );
}
